import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Tag,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Modal,
  Divider,
  message,
  Badge,
  Tooltip,
  Progress,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  PhoneOutlined,
  MessageOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CarOutlined,
  CompassOutlined,
} from '@ant-design/icons';
import { deliveryService } from '../services/deliveryService';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [trackingModalVisible, setTrackingModalVisible] = useState(false);
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [messageType, setMessageType] = useState('text');

  const fetchOrders = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      const response = await deliveryService.getOrders(params);
      if (response.data.success && response.data.data) {
        setOrders(response.data.data);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'assigned':
        return 'blue';
      case 'in_progress':
        return 'processing';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'delivered':
        return 'success';
      case 'out_for_delivery':
        return 'warning';
      case 'pending':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'assigned':
        return <ClockCircleOutlined />;
      case 'in_progress':
        return <CarOutlined />;
      case 'completed':
        return <CheckCircleOutlined />;
      case 'cancelled':
        return <ExclamationCircleOutlined />;
      case 'delivered':
        return <CheckCircleOutlined />;
      case 'out_for_delivery':
        return <CarOutlined />;
      case 'pending':
        return <ClockCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'normal':
        return 'blue';
      case 'low':
        return 'default';
      default:
        return 'blue';
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setDetailModalVisible(true);
  };

  const startDelivery = (orderId) => {
    setOrders(
      orders.map((order) => (order.id === orderId ? { ...order, status: 'in_progress' } : order)),
    );
    message.success('Delivery started!');
  };

  const completeDelivery = (orderId) => {
    setOrders(
      orders.map((order) =>
        order.id === orderId
          ? { ...order, status: 'completed', completedTime: new Date().toLocaleString() }
          : order,
      ),
    );
    message.success('Delivery completed!');
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const response = await deliveryService.updateOrderStatus(orderId, newStatus);
      if (response.data.success) {
        message.success(`Order status updated to ${newStatus.replace('_', ' ').toUpperCase()}`);
        // Update the specific order in state immediately
        setOrders((prev) =>
          prev.map((order) => (order.id === orderId ? { ...order, status: newStatus } : order)),
        );
      } else {
        message.error('Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      message.error('Failed to update order status');
    }
  };

  const openChat = async (order) => {
    setSelectedOrder(order);
    setChatModalVisible(true);
    try {
      const response = await deliveryService.getOrderChat(order.id);
      if (response.data.success) {
        setChatMessages(response.data.data);
      }
    } catch (error) {
      message.error('Failed to load chat messages');
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedOrder) return;

    try {
      const response = await deliveryService.sendMessageToCustomer(selectedOrder.id, {
        message: messageInput,
        type: messageType,
      });

      if (response.data.success) {
        setChatMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            sender: 'delivery_person',
            message: messageInput,
            type: messageType,
            timestamp: new Date().toISOString(),
          },
        ]);
        setMessageInput('');
        message.success('Message sent successfully');
      }
    } catch (error) {
      message.error('Failed to send message');
    }
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (name, record) => (
        <div>
          <div>{name}</div>
          <Text type="secondary" style={{ fontSize: '0.9rem' }}>
            {record.customer_phone}
          </Text>
        </div>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (address) => (
        <Tooltip title={address}>
          <Text ellipsis style={{ maxWidth: '200px' }}>
            {address}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status ? status.replace('_', ' ').toUpperCase() : 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>{priority ? priority.toUpperCase() : 'NORMAL'}</Tag>
      ),
    },
    {
      title: 'Distance',
      dataIndex: 'distance',
      key: 'distance',
      render: (distance) => distance || 'N/A',
    },
    {
      title: 'Delivery Fee',
      dataIndex: 'delivery_charge',
      key: 'delivery_charge',
      render: (fee) => <Text strong>NPR {fee ? parseFloat(fee).toFixed(2) : '0.00'}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => viewOrderDetails(record)}
          />
          <Button
            type="text"
            icon={<MessageOutlined />}
            size="small"
            onClick={() => openChat(record)}
            title="Chat with Customer"
          />
          <Select
            placeholder="Status"
            size="small"
            style={{ width: 100, minWidth: 100 }}
            onChange={(value) => handleStatusChange(record.id, value)}
            value={record.status}
          >
            <Option value="pending">Pending</Option>
            <Option value="out_for_delivery">Out</Option>
            <Option value="delivered">Done</Option>
            <Option value="cancelled">Cancel</Option>
          </Select>
          {record.status === 'out_for_delivery' && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              size="small"
              onClick={() => handleStatusChange(record.id, 'delivered')}
            />
          )}
        </Space>
      ),
    },
  ];

  const assignedOrders = orders.filter((order) => order.status === 'pending');
  const inProgressOrders = orders.filter((order) => order.status === 'out_for_delivery');
  const completedOrders = orders.filter((order) => order.status === 'delivered');

  return (
    <div style={{ padding: '16px' }}>
      <Title level={2} style={{ fontSize: '1.5rem', marginBottom: '8px' }}>
        Delivery Orders
      </Title>
      <Paragraph style={{ color: '#666', marginBottom: '20px', fontSize: '0.9rem' }}>
        Manage your delivery assignments and track order status
      </Paragraph>

      {/* Stats Cards - Mobile Responsive */}
      <Row gutter={[12, 12]} style={{ marginBottom: '20px' }}>
        <Col xs={8} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Badge count={assignedOrders.length} size="small">
              <ClockCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            </Badge>
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{assignedOrders.length}</div>
              <div style={{ color: '#666', fontSize: '0.8rem' }}>Assigned</div>
            </div>
          </Card>
        </Col>
        <Col xs={8} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Badge count={inProgressOrders.length} size="small">
              <CarOutlined style={{ fontSize: '24px', color: '#faad14' }} />
            </Badge>
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                {inProgressOrders.length}
              </div>
              <div style={{ color: '#666', fontSize: '0.8rem' }}>In Progress</div>
            </div>
          </Card>
        </Col>
        <Col xs={8} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Badge count={completedOrders.length} size="small">
              <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
            </Badge>
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{completedOrders.length}</div>
              <div style={{ color: '#666', fontSize: '0.8rem' }}>Completed</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filters - Mobile Responsive */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search orders..."
              prefix={<SearchOutlined />}
              allowClear
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Status"
              style={{ width: '100%' }}
              allowClear
              size="small"
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value || '')}
            >
              <Option value="pending">Pending</Option>
              <Option value="out_for_delivery">Out for Delivery</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select placeholder="Priority" style={{ width: '100%' }} allowClear size="small">
              <Option value="high">High</Option>
              <Option value="normal">Normal</Option>
              <Option value="low">Low</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker style={{ width: '100%' }} size="small" />
          </Col>
        </Row>
      </Card>

      {/* Orders Table - Mobile Responsive */}
      <Card size="small">
        <Table
          columns={columns}
          dataSource={Array.isArray(orders) ? orders : []}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} deliveries`,
          }}
        />
      </Card>

      {/* Order Details Modal */}
      <Modal
        title={`Order Details - ${selectedOrder?.id}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          selectedOrder?.status === 'assigned' && (
            <Button
              key="start"
              type="primary"
              icon={<CarOutlined />}
              onClick={() => {
                startDelivery(selectedOrder.id);
                setDetailModalVisible(false);
              }}
            >
              Start Delivery
            </Button>
          ),
          selectedOrder?.status === 'in_progress' && (
            <Button
              key="complete"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                completeDelivery(selectedOrder.id);
                setDetailModalVisible(false);
              }}
            >
              Complete Delivery
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedOrder && (
          <div>
            <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12}>
                <div>
                  <Text strong>Customer:</Text> {selectedOrder.customer_name || 'N/A'}
                </div>
                <div>
                  <Text strong>Phone:</Text> {selectedOrder.customer_phone || 'N/A'}
                </div>
                <div>
                  <Text strong>Booking Contact:</Text>{' '}
                  {selectedOrder.customer_contact_phone || 'N/A'}
                </div>
                <div>
                  <Text strong>Address:</Text> {selectedOrder.address || 'N/A'}
                </div>
                {selectedOrder.service_address && (
                  <div>
                    <Text strong>Service Address:</Text> {selectedOrder.service_address}
                  </div>
                )}
              </Col>
              <Col xs={24} sm={12}>
                <div>
                  <Text strong>Order ID:</Text> {selectedOrder.id}
                </div>
                <div>
                  <Text strong>Status:</Text>{' '}
                  <Tag color={getStatusColor(selectedOrder.status)}>
                    {selectedOrder.status
                      ? selectedOrder.status.replace('_', ' ').toUpperCase()
                      : 'N/A'}
                  </Tag>
                </div>
                <div>
                  <Text strong>Priority:</Text>{' '}
                  <Tag color={getPriorityColor(selectedOrder.priority)}>
                    {selectedOrder.priority ? selectedOrder.priority.toUpperCase() : 'NORMAL'}
                  </Tag>
                </div>
                {selectedOrder.service_date && (
                  <div>
                    <Text strong>Service Date:</Text> {selectedOrder.service_date}
                  </div>
                )}
                {selectedOrder.service_time && (
                  <div>
                    <Text strong>Service Time:</Text> {selectedOrder.service_time}
                  </div>
                )}
              </Col>
            </Row>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Special Instructions:</Text>
              <div style={{ marginTop: '8px' }}>{selectedOrder.specialInstructions || 'None'}</div>
            </div>

            <Divider />

            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12}>
                <div>
                  <Text strong>Total Amount:</Text> NPR{' '}
                  {parseFloat(selectedOrder?.total_amount || 0).toFixed(2)}
                </div>
                <div>
                  <Text strong>Your Earnings:</Text>{' '}
                  <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
                    NPR {parseFloat(selectedOrder?.delivery_charge || 0).toFixed(2)}
                  </Text>
                </div>
                <div>
                  <Text strong>Delivery Rate:</Text>{' '}
                  {selectedOrder?.delivery_charge
                    ? `${((parseFloat(selectedOrder.delivery_charge) / parseFloat(selectedOrder.total_amount || 1)) * 100).toFixed(1)}% of order value`
                    : 'N/A'}
                </div>
                <div>
                  <Text strong>Distance:</Text> {selectedOrder.distance || '0 km'}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div>
                  <Text strong>Payment Method:</Text>{' '}
                  {selectedOrder.payment_method === 'cod'
                    ? 'Cash on Delivery'
                    : selectedOrder.payment_method || 'N/A'}
                </div>
                <div>
                  <Text strong>Assigned Time:</Text>{' '}
                  {selectedOrder.created_at
                    ? new Date(selectedOrder.created_at).toLocaleString()
                    : 'N/A'}
                </div>
                <div>
                  <Text strong>Estimated Delivery:</Text>{' '}
                  {selectedOrder.estimated_delivery || '30-45 minutes'}
                </div>
              </Col>
            </Row>

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <Button
                  icon={<PhoneOutlined />}
                  onClick={() => {
                    const phone =
                      selectedOrder?.customer_contact_phone || selectedOrder?.customer_phone;
                    if (phone) {
                      navigator.clipboard.writeText(phone);
                      window.location.href = `tel:${phone}`;
                      message.success('Calling customer...');
                    } else {
                      message.error('Customer phone number not available');
                    }
                  }}
                >
                  Call Customer
                </Button>
                <Button
                  icon={<MessageOutlined />}
                  onClick={() => {
                    setDetailModalVisible(false);
                    openChat(selectedOrder);
                  }}
                >
                  Send Message
                </Button>
                <Button
                  icon={<EnvironmentOutlined />}
                  onClick={() => {
                    if (
                      selectedOrder?.delivery_address_details?.latitude &&
                      selectedOrder?.delivery_address_details?.longitude
                    ) {
                      const { latitude, longitude } = selectedOrder.delivery_address_details;
                      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
                      window.open(googleMapsUrl, '_blank');
                      message.success('Opening directions to customer location...');
                    } else {
                      message.error('Customer location not available');
                    }
                  }}
                >
                  Get Directions
                </Button>
              </Space>
              {selectedOrder.status === 'in_progress' && (
                <Button
                  type="primary"
                  icon={<CompassOutlined />}
                  onClick={() => {
                    setDetailModalVisible(false);
                    setTrackingModalVisible(true);
                  }}
                >
                  Live Tracking
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Live Tracking Modal */}
      <Modal
        title="Live Delivery Tracking"
        open={trackingModalVisible}
        onCancel={() => setTrackingModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTrackingModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedOrder && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div
                style={{
                  width: '120px',
                  height: '120px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '50%',
                  margin: '0 auto 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CompassOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
              </div>
              <Title level={4}>Order {selectedOrder.id}</Title>
              <Paragraph>Tracking your delivery in real-time</Paragraph>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Text strong>Delivery Progress</Text>
              <Progress
                percent={75}
                status="active"
                strokeColor="#1890ff"
                style={{ marginTop: '16px' }}
              />
            </div>

            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12}>
                <Card size="small" title="Current Location">
                  <div>
                    <Text strong>Address:</Text> 123 Main St, Current City
                    <br />
                    <Text strong>Distance to destination:</Text> 1.2 km
                    <br />
                    <Text strong>Estimated arrival:</Text> 15 minutes
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" title="Customer Information">
                  <div>
                    <Text strong>Name:</Text> {selectedOrder.customer_name || 'N/A'}
                    <br />
                    <Text strong>Phone:</Text>{' '}
                    {selectedOrder.customer_contact_phone || selectedOrder.customer_phone || 'N/A'}
                    <br />
                    <Text strong>Booking Contact:</Text>{' '}
                    {selectedOrder.customer_contact_phone || 'N/A'}
                    <br />
                    <Text strong>Destination:</Text>{' '}
                    {selectedOrder.service_address || selectedOrder.address || 'N/A'}
                    <br />
                    {selectedOrder.service_date && (
                      <>
                        <Text strong>Service Date:</Text> {selectedOrder.service_date}
                        <br />
                      </>
                    )}
                    {selectedOrder.service_time && (
                      <>
                        <Text strong>Service Time:</Text> {selectedOrder.service_time}
                        <br />
                      </>
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Chat Modal */}
      <Modal
        title={`Chat with Customer - Order ${selectedOrder?.id}`}
        open={chatModalVisible}
        onCancel={() => setChatModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setChatModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedOrder && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Customer:</Text> {selectedOrder.customer_name}
            </div>

            <div
              style={{
                height: '300px',
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: '#fafafa',
                marginBottom: '16px',
              }}
            >
              {chatMessages.map((message) => {
                const deliveryPerson = JSON.parse(localStorage.getItem('delivery_user') || '{}');
                const isFromDelivery = message.sender_id === deliveryPerson.id;
                return (
                  <div
                    key={message.id}
                    style={{
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: isFromDelivery ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '70%',
                        padding: '8px',
                        backgroundColor: isFromDelivery ? '#e6f7ff' : '#f6ffed',
                        borderRadius: '4px',
                      }}
                    >
                      <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '4px' }}>
                        {isFromDelivery ? 'You' : 'Customer'} -{' '}
                        {new Date(message.created_at).toLocaleString()}
                      </div>
                      <div>{message.message}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Row gutter={[8, 8]}>
              <Col xs={16} sm={16}>
                <Input.TextArea
                  placeholder="Type your message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onPressEnter={sendMessage}
                  style={{ marginBottom: '8px' }}
                />
              </Col>
              <Col xs={8} sm={8}>
                <Space>
                  <Select value={messageType} onChange={setMessageType} style={{ width: 120 }}>
                    <Option value="text">Text</Option>
                    <Option value="location_update">Location</Option>
                    <Option value="delivery_photo">Photo</Option>
                  </Select>
                  <Button
                    type="primary"
                    icon={<MessageOutlined />}
                    onClick={sendMessage}
                    disabled={!messageInput.trim()}
                  >
                    Send
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Orders;
